import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import SportsScoreRoundedIcon from '@mui/icons-material/SportsScoreRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import type { IdealLineReference, KioskAccountSessionClaim, User } from '@kangaroo/shared';
import { PaddiqLogo } from '@kangaroo/ui';
import { useNavigate } from 'react-router-dom';

import { SessionsCompareMap } from '../../components/sessions/SessionsCompareMap';
import { SessionSelectorDrawer } from '../../components/sessions/SessionSelectorDrawer';
import { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import { pickDefaultLapNumber } from '../../features/sessions/sessionContent';
import type { SessionListItem } from '../../features/sessions/sessionList';
import { createCompareSlot, type CompareSlotState } from '../../features/sessions/compareSlots';
import { getApiUrl } from '../../lib/apiUrl';
import {
  createKioskAccountTerminationLock,
  isCorruptKioskAccountTerminationLock,
  isKioskAccountSessionClaim,
  nextKioskBrowserLease,
  persistKioskAccountTerminationLock,
  receiptMatchesKioskTerminationLock,
  removeKioskAccountTerminationLock,
  restoreKioskAccountTerminationLock,
  startKioskAccountTerminationRetry,
  type KioskAccountTerminationLock,
  type KioskAccountTerminationReceipt,
} from '../../lib/kioskAccountSession';
import { buildExactSessionDestination } from '../../lib/sessionScanDestination';
import { isMyChronSessionExport, type MyChronSessionExport } from '../../lib/telemetrySession';
import { withGpsDerivedTelemetry } from '../../lib/gpsDerivedTelemetry';
import { loadGoogleMaps } from '../../lib/mapsLoader';
import { getGoogleMapsApiKey } from '../../lib/runtimeConfig';
import { useAuthStore } from '../../store/authStore';
import {
  beginKioskPrivacyClear,
  idleKioskClearState,
  kioskIdleGrantIdentity,
  kioskClearReducer,
  removeKioskClearLock,
  restoreKioskClearState,
  shouldAcceptKioskDisplayPoll,
  startKioskIdleClearTimers,
  startKioskClearRetryLoop,
  type KioskClearAction,
} from './kioskClearLifecycle';
import {
  kioskSessionDirectoryHasMore,
  kioskSessionDirectorySessions,
  mergeKioskSessionPage,
  type KioskSessionDirectory,
  type KioskSessionPage,
  type KioskSessionSummary,
} from './kioskSessionDirectory';

type Station = { id: string; name: string };
type Display = {
  grantId: string;
  expiresAt: string;
  session: KioskSessionSummary;
};
type PendingDisplay = { id: string; expiresAt: string; session: Display['session'] };
type IdlePanel = 'setup' | 'session';
type SessionDirectoryState = {
  directory: KioskSessionDirectory | null;
  status: 'idle' | 'loading' | 'ready' | 'loading-more' | 'error';
  error: string | null;
};

const IDLE_MS = 3 * 60_000;
const WARNING_MS = 15_000;
const KIOSK_SESSION_PAGE_SIZE = 50;
const SETUP_PANEL_DURATION_MS = 30_000;
const EMPTY_SESSION_DIRECTORY: SessionDirectoryState = {
  directory: null,
  status: 'idle',
  error: null,
};
const IOS_APP_STORE_URL = 'https://apps.apple.com/app/paddiq/id6785660120';

function credentialFromBrowser() {
  const params = new URLSearchParams(window.location.search);
  const incoming = params.get('credential');
  if (incoming) {
    localStorage.setItem('paddiq:kiosk-credential', incoming);
    window.history.replaceState({}, '', '/kiosk');
    return incoming;
  }
  return localStorage.getItem('paddiq:kiosk-credential');
}

function localStorageFromBrowser() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function kioskRequest<T>(path: string, credential: string, init?: RequestInit): Promise<T> {
  const response = await fetch(getApiUrl(`/kiosk${path}`), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Kiosk-Credential': credential,
      ...init?.headers,
    },
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error('This browser is not enrolled as a kiosk.');
    if (response.status === 409)
      throw new Error('The session list changed while loading. Reload it to continue.');
    throw new Error('The kiosk service is unavailable.');
  }
  return response.status === 204 ? (undefined as T) : (response.json() as Promise<T>);
}

function formatLap(ms: number | null) {
  if (ms == null) return '—';
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}:${((ms % 60_000) / 1000).toFixed(3).padStart(6, '0')}`;
}

export function KioskPage() {
  const navigate = useNavigate();
  const setKioskSession = useAuthStore((state) => state.setKioskSession);
  const initialCredential = useMemo(credentialFromBrowser, []);
  const clearStorage = useMemo(localStorageFromBrowser, []);
  const [credential, setCredential] = useState<string | null>(initialCredential);
  const [station, setStation] = useState<Station | null>(null);
  const [display, setDisplay] = useState<Display | null>(null);
  const [sessionDirectory, setSessionDirectory] =
    useState<SessionDirectoryState>(EMPTY_SESSION_DIRECTORY);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [qr, setQr] = useState<{ qrDataUrl: string; expiresAt: string } | null>(null);
  const [session, setSession] = useState<MyChronSessionExport | null>(null);
  const [lapNumber, setLapNumber] = useState<number | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null);
  const [pendingDisplay, setPendingDisplay] = useState<PendingDisplay | null>(null);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const [confirmationSubmitting, setConfirmationSubmitting] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'locating' | 'active' | 'unavailable'>(
    'locating',
  );
  const [stationLocation, setStationLocation] = useState<{
    latitude: number;
    longitude: number;
    accuracyMeters: number;
  } | null>(null);
  const [locationLabel, setLocationLabel] = useState<string | null>(null);
  const [warning, setWarning] = useState(false);
  const [idlePanel, setIdlePanel] = useState<IdlePanel>('setup');
  const [setupPanelRun, setSetupPanelRun] = useState(0);
  const [setupSecondsRemaining, setSetupSecondsRemaining] = useState(30);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compareSlots, setCompareSlots] = useState<CompareSlotState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [clearState, dispatchClear] = useReducer(kioskClearReducer, idleKioskClearState);
  const [accountTermination, setAccountTermination] = useState<{
    lock: KioskAccountTerminationLock;
    status: 'clearing' | 'retrying' | 'exhausted';
    attempt: number;
  } | null>(() => {
    const lock = restoreKioskAccountTerminationLock(clearStorage);
    return lock ? { lock, status: 'clearing', attempt: 0 } : null;
  });
  const [accountTerminationRun, setAccountTerminationRun] = useState(0);
  const accountTerminationAttempt = useRef<{
    key: string;
    cancel: () => void;
  } | null>(null);
  const accountTerminationDisposeTimer = useRef<ReturnType<typeof setTimeout>>();
  const idleTimers = useRef<{ restart: () => void; cancel: () => void }>();
  const clearStateRef = useRef(clearState);
  const pollEpoch = useRef(0);
  const sessionDirectoryEpoch = useRef(0);
  const geocodedLocationKey = useRef<string | null>(null);
  const displayGrantId = kioskIdleGrantIdentity(display);
  const displayExpiresAt = display?.expiresAt ?? null;
  const sessions = useMemo<SessionListItem[]>(
    () =>
      kioskSessionDirectorySessions(sessionDirectory.directory, display?.session ?? null).map(
        (item) => ({
          id: item.id,
          title: item.trackName,
          subtitle: `${item.driverName} · ${item.lapCount} laps · Best ${formatLap(item.bestLapMs)}`,
          recordedAtMs: item.recordedAtMs,
          trackName: item.trackName,
          driverId: null,
          driverName: item.driverName,
          driverAvatarUrl: null,
        }),
      ),
    [display?.session, sessionDirectory.directory],
  );

  const transitionClear = useCallback((action: KioskClearAction) => {
    clearStateRef.current = kioskClearReducer(clearStateRef.current, action);
    dispatchClear(action);
  }, []);

  const blankDisplay = useCallback(() => {
    setDisplay(null);
    sessionDirectoryEpoch.current += 1;
    setSessionDirectory(EMPTY_SESSION_DIRECTORY);
    setSelectedSessionId(null);
    setSession(null);
    setLapNumber(null);
    setSessionLoading(false);
    setSessionLoadError(null);
    setWarning(false);
  }, []);

  const clearDisplay = useCallback(() => {
    if (!credential || !station || !displayGrantId || !displayExpiresAt) return;
    const lock = { stationId: station.id, grantId: displayGrantId, expiresAt: displayExpiresAt };
    beginKioskPrivacyClear({
      lock,
      storage: clearStorage,
      fencePolls: () => {
        pollEpoch.current += 1;
      },
      enterPrivacyLock: (nextLock) => transitionClear({ type: 'begin', lock: nextLock }),
      blankTelemetry: blankDisplay,
    });
  }, [
    blankDisplay,
    clearStorage,
    credential,
    displayExpiresAt,
    displayGrantId,
    station,
    transitionClear,
  ]);

  useEffect(() => {
    const lock = clearState.lock;
    if (!credential || !lock) return;

    const finish = () => {
      removeKioskClearLock(clearStorage, lock);
      pollEpoch.current += 1;
      const replacement = restoreKioskClearState(clearStorage, lock.stationId);
      transitionClear(
        replacement.lock ? { type: 'begin', lock: replacement.lock } : { type: 'confirmed' },
      );
    };
    return startKioskClearRetryLoop({
      lock,
      initialAttempt: clearState.attempt,
      request: (signal) =>
        kioskRequest('/clear', credential, {
          method: 'POST',
          body: JSON.stringify({ grantId: lock.grantId }),
          signal,
        }),
      onRetry: () => transitionClear({ type: 'failed' }),
      onFinished: finish,
    });
  }, [
    clearState.lock?.expiresAt,
    clearState.lock?.grantId,
    clearStorage,
    credential,
    transitionClear,
  ]);

  useEffect(() => {
    const lock = accountTermination?.lock;
    if (!credential || !station || !lock) return;
    if (isCorruptKioskAccountTerminationLock(lock)) {
      setAccountTermination((current) => (current ? { ...current, status: 'exhausted' } : current));
      return;
    }
    if (station.id !== lock.stationId) {
      setAccountTermination((current) => (current ? { ...current, status: 'exhausted' } : current));
      return;
    }

    const attemptKey = [
      lock.stationId,
      lock.session.accountSessionId,
      lock.session.accountSessionGeneration,
      accountTerminationRun,
    ].join(':');
    if (accountTerminationAttempt.current?.key === attemptKey) return;
    accountTerminationAttempt.current?.cancel();
    const cancel = startKioskAccountTerminationRetry({
      request: async (signal) => {
        const response = await kioskRequest<{ data: KioskAccountTerminationReceipt }>(
          '/account-session/terminate',
          credential,
          {
            method: 'POST',
            body: JSON.stringify({ session: lock.session, reason: lock.reason }),
            signal,
          },
        );
        if (!receiptMatchesKioskTerminationLock(response.data, lock)) {
          throw new Error('Termination receipt did not match the concealed account session');
        }
        return response.data;
      },
      onRetry: (attempt) =>
        setAccountTermination((current) =>
          current ? { ...current, status: 'retrying', attempt } : current,
        ),
      onConfirmed: () => {
        accountTerminationAttempt.current = null;
        removeKioskAccountTerminationLock(clearStorage, lock);
        pollEpoch.current += 1;
        setDisplay(null);
        setPendingDisplay(null);
        setQr(null);
        setError(null);
        setAccountTermination(null);
      },
      onExhausted: () => {
        accountTerminationAttempt.current = null;
        setAccountTermination((current) =>
          current ? { ...current, status: 'exhausted' } : current,
        );
      },
    });
    accountTerminationAttempt.current = { key: attemptKey, cancel };
  }, [
    accountTermination?.lock.session.accountSessionGeneration,
    accountTermination?.lock.session.accountSessionId,
    accountTerminationRun,
    clearStorage,
    credential,
    station,
  ]);

  useEffect(() => {
    // React development mode replays effects immediately after mounting. Defer disposal so
    // that replay retains the exact in-flight privacy request instead of issuing a duplicate.
    // A real unmount still cancels the attempt on the next task.
    if (accountTerminationDisposeTimer.current) {
      clearTimeout(accountTerminationDisposeTimer.current);
      accountTerminationDisposeTimer.current = undefined;
    }
    return () => {
      const attempt = accountTerminationAttempt.current;
      if (!import.meta.env.DEV) {
        attempt?.cancel();
        accountTerminationAttempt.current = null;
        return;
      }
      accountTerminationDisposeTimer.current = setTimeout(() => {
        if (accountTerminationAttempt.current !== attempt) return;
        attempt?.cancel();
        accountTerminationAttempt.current = null;
        accountTerminationDisposeTimer.current = undefined;
      }, 0);
    };
  }, []);

  const resetIdle = useCallback(() => {
    setWarning(false);
    idleTimers.current?.restart();
  }, []);

  useEffect(() => {
    if (!credential) {
      if (!import.meta.env.DEV) {
        setError('This browser is not enrolled as a kiosk.');
        return;
      }

      void fetch(getApiUrl('/kiosk/dev-enroll'), { method: 'POST' })
        .then(async (response) => {
          if (!response.ok) throw new Error('Unable to enroll the local kiosk.');
          return response.json() as Promise<{ data: { credential: string } }>;
        })
        .then((result) => {
          localStorage.setItem('paddiq:kiosk-credential', result.data.credential);
          setCredential(result.data.credential);
        })
        .catch((reason: Error) => setError(reason.message));
      return;
    }
    void kioskRequest<{ data: Station }>('/me', credential)
      .then((result) => {
        const restored = restoreKioskClearState(clearStorage, result.data.id);
        if (restored.lock) {
          pollEpoch.current += 1;
          transitionClear({ type: 'begin', lock: restored.lock });
        }
        const accountLock = restoreKioskAccountTerminationLock(clearStorage);
        if (accountLock) {
          pollEpoch.current += 1;
          setDisplay(null);
          setPendingDisplay(null);
          setAccountTermination(
            (current) => current ?? { lock: accountLock, status: 'clearing', attempt: 0 },
          );
        }
        setStation(result.data);
      })
      .catch((reason: Error) => {
        if (import.meta.env.DEV && reason.message === 'This browser is not enrolled as a kiosk.') {
          clearStorage?.removeItem('paddiq:kiosk-credential');
          setError(null);
          setCredential(null);
          return;
        }
        setError(reason.message);
      });
  }, [clearStorage, credential, transitionClear]);

  useEffect(() => {
    if (!credential || !station || accountTermination) return;
    let cancelled = false;
    const refresh = async () => {
      const requestEpoch = pollEpoch.current;
      try {
        const result = await kioskRequest<{ data: Display | null }>('/current-display', credential);
        if (
          !cancelled &&
          shouldAcceptKioskDisplayPoll({
            clearState: clearStateRef.current,
            requestEpoch,
            currentEpoch: pollEpoch.current,
          })
        ) {
          setDisplay(result.data);
          if (result.data) setSelectedSessionId((current) => current ?? result.data!.session.id);
        }
      } catch (reason) {
        if (
          !cancelled &&
          shouldAcceptKioskDisplayPoll({
            clearState: clearStateRef.current,
            requestEpoch,
            currentEpoch: pollEpoch.current,
          })
        ) {
          setError(reason instanceof Error ? reason.message : 'The kiosk service is unavailable.');
        }
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 3000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [accountTermination, credential, station]);

  useEffect(() => {
    if (!stationLocation) {
      setLocationLabel(null);
      geocodedLocationKey.current = null;
      return;
    }
    const lookupKey = `${stationLocation.latitude.toFixed(3)},${stationLocation.longitude.toFixed(3)}`;
    if (geocodedLocationKey.current === lookupKey) return;
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey) return;
    geocodedLocationKey.current = lookupKey;
    let cancelled = false;
    void loadGoogleMaps(apiKey, { includePlaces: false })
      .then(() =>
        new google.maps.Geocoder().geocode({
          location: { lat: stationLocation.latitude, lng: stationLocation.longitude },
        }),
      )
      .then(({ results }) => {
        if (cancelled) return;
        const components = results[0]?.address_components ?? [];
        const component = (type: string, short = false) => {
          const match = components.find((item) => item.types.includes(type));
          return match ? (short ? match.short_name : match.long_name) : '';
        };
        const town =
          component('locality') ||
          component('postal_town') ||
          component('administrative_area_level_2');
        const region = component('administrative_area_level_1', true);
        const postalCode = component('postal_code');
        const country = component('country', true);
        const locality = [town, region].filter(Boolean).join(', ');
        setLocationLabel(
          [locality, postalCode, country && country !== 'US' ? country : '']
            .filter(Boolean)
            .join(' '),
        );
      })
      .catch(() => {
        if (!cancelled) setLocationLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, [stationLocation?.latitude, stationLocation?.longitude]);

  useEffect(() => {
    if (!credential || !station || accountTermination) return;
    let cancelled = false;
    const refresh = () =>
      kioskRequest<{ data: PendingDisplay | null }>('/pending-display', credential)
        .then((result) => {
          if (cancelled) return;
          setPendingDisplay(result.data);
          if (!result.data) {
            setConfirmationCode('');
            setConfirmationError(null);
          }
        })
        .catch(() => undefined);
    void refresh();
    const interval = window.setInterval(() => void refresh(), 1500);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [accountTermination, credential, station]);

  const confirmDisplay = useCallback(async () => {
    if (
      !credential ||
      !station ||
      accountTermination ||
      confirmationSubmitting ||
      confirmationCode.length !== 6
    )
      return;
    setConfirmationError(null);
    setConfirmationSubmitting(true);
    let confirmedAccountSession: KioskAccountSessionClaim | null = null;
    try {
      const browserLease = nextKioskBrowserLease(clearStorage, station.id);
      const response = await kioskRequest<{
        data: {
          token: string;
          user: User;
          sessionId: string;
          accountSession: KioskAccountSessionClaim;
          accountSessionExpiresAt: string;
        };
      }>('/confirm-display', credential, {
        method: 'POST',
        body: JSON.stringify({
          confirmationCode,
          browserLeaseId: browserLease.id,
          browserLeaseGeneration: browserLease.generation,
        }),
      });
      if (
        !isKioskAccountSessionClaim(response.data.accountSession) ||
        response.data.accountSession.stationId !== station.id ||
        response.data.accountSession.browserLeaseId !== browserLease.id ||
        response.data.accountSession.browserLeaseGeneration !== browserLease.generation
      ) {
        throw new Error('Kiosk account session identity is unavailable');
      }
      confirmedAccountSession = response.data.accountSession;
      const destination = buildExactSessionDestination(response.data.sessionId);
      if (!destination) throw new Error('Invalid exact session destination');
      await setKioskSession(response.data.token, response.data.user, response.data.accountSession);
      navigate(destination, { replace: true });
    } catch {
      if (confirmedAccountSession) {
        const lock = createKioskAccountTerminationLock(confirmedAccountSession, 'EXPLICIT_CLEAR');
        persistKioskAccountTerminationLock(clearStorage, lock);
        void useAuthStore.getState().clearSession('EXPLICIT_CLEAR');
        setPendingDisplay(null);
        setAccountTermination({ lock, status: 'clearing', attempt: 0 });
      } else {
        setConfirmationError('That code is incorrect, expired, or requires a kiosk update.');
      }
    } finally {
      setConfirmationSubmitting(false);
    }
  }, [
    accountTermination,
    clearStorage,
    confirmationCode,
    confirmationSubmitting,
    credential,
    navigate,
    setKioskSession,
    station,
  ]);

  useEffect(() => {
    if (!credential || !station) return;
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }
    let cancelled = false;
    const publish = () =>
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          setStationLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
          });
          void kioskRequest('/location', credential, {
            method: 'POST',
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracyMeters: position.coords.accuracy,
            }),
          })
            .then(() => setLocationStatus('active'))
            .catch(() => setLocationStatus('unavailable'));
        },
        () => {
          setStationLocation(null);
          setLocationStatus('unavailable');
        },
        { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
      );
    publish();
    const interval = window.setInterval(publish, 20_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [credential, station]);

  const reloadSessionDirectory = useCallback(() => {
    if (!credential || !displayGrantId) return;
    const requestEpoch = ++sessionDirectoryEpoch.current;
    setSessionDirectory({ directory: null, status: 'loading', error: null });
    void kioskRequest<KioskSessionPage>(
      `/sessions?page=1&perPage=${KIOSK_SESSION_PAGE_SIZE}`,
      credential,
    )
      .then((page) => {
        if (requestEpoch !== sessionDirectoryEpoch.current) return;
        setSessionDirectory({
          directory: mergeKioskSessionPage(null, page),
          status: 'ready',
          error: null,
        });
      })
      .catch((reason: unknown) => {
        if (requestEpoch !== sessionDirectoryEpoch.current) return;
        setSessionDirectory({
          directory: null,
          status: 'error',
          error: reason instanceof Error ? reason.message : 'Unable to load the session directory.',
        });
      });
  }, [credential, displayGrantId]);

  useEffect(() => {
    if (!credential || !displayGrantId) {
      sessionDirectoryEpoch.current += 1;
      setSessionDirectory(EMPTY_SESSION_DIRECTORY);
      return;
    }
    reloadSessionDirectory();
  }, [credential, displayGrantId, reloadSessionDirectory]);

  const loadMoreSessions = useCallback(() => {
    const directory = sessionDirectory.directory;
    if (
      !credential ||
      !directory ||
      sessionDirectory.status === 'loading-more' ||
      !kioskSessionDirectoryHasMore(directory)
    )
      return;

    const requestEpoch = sessionDirectoryEpoch.current;
    const nextPage = directory.page + 1;
    const query = new URLSearchParams({
      page: String(nextPage),
      perPage: String(directory.perPage),
      snapshot: directory.snapshot,
    });
    setSessionDirectory({ directory, status: 'loading-more', error: null });
    void kioskRequest<KioskSessionPage>(`/sessions?${query.toString()}`, credential)
      .then((page) => {
        if (requestEpoch !== sessionDirectoryEpoch.current) return;
        setSessionDirectory((current) => {
          if (current.directory !== directory) return current;
          try {
            return {
              directory: mergeKioskSessionPage(directory, page),
              status: 'ready',
              error: null,
            };
          } catch (reason) {
            return {
              directory,
              status: 'error',
              error: reason instanceof Error ? reason.message : 'Unable to load more sessions.',
            };
          }
        });
      })
      .catch((reason: unknown) => {
        if (requestEpoch !== sessionDirectoryEpoch.current) return;
        setSessionDirectory((current) =>
          current.directory === directory
            ? {
                directory,
                status: 'error',
                error: reason instanceof Error ? reason.message : 'Unable to load more sessions.',
              }
            : current,
        );
      });
  }, [credential, sessionDirectory]);

  const loadKioskSession = useCallback(
    async (sessionId: string) => {
      if (!credential) return null;
      const payload = await kioskRequest<unknown>(
        `/data-files/${encodeURIComponent(sessionId)}/content`,
        credential,
      );
      return isMyChronSessionExport(payload) ? withGpsDerivedTelemetry(payload) : null;
    },
    [credential],
  );

  const loadKioskIdealLineReference = useCallback(
    async (sessionId: string) => {
      if (!credential) throw new Error('This browser is not enrolled as a kiosk.');
      const response = await kioskRequest<{ data: IdealLineReference }>(
        `/sessions/${encodeURIComponent(sessionId)}/ideal-line`,
        credential,
      );
      return response.data;
    },
    [credential],
  );

  useEffect(() => {
    if (!credential || !station || display || clearState.lock || accountTermination) return;
    let cancelled = false;
    const create = () =>
      kioskRequest<{ data: { qrDataUrl: string; expiresAt: string } }>(
        '/pairing-token',
        credential,
        { method: 'POST' },
      )
        .then((result) => {
          if (!cancelled) setQr(result.data);
        })
        .catch((reason: Error) => {
          if (!cancelled) setError(reason.message);
        });
    void create();
    const interval = window.setInterval(() => void create(), 75_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [accountTermination, clearState.lock, credential, display, station]);

  useEffect(() => {
    if (display || idlePanel !== 'setup') return;
    const startedAt = Date.now();
    setSetupSecondsRemaining(30);
    const updateRemaining = () => {
      setSetupSecondsRemaining(
        Math.max(0, Math.ceil((SETUP_PANEL_DURATION_MS - (Date.now() - startedAt)) / 1000)),
      );
    };
    const interval = window.setInterval(updateRemaining, 1_000);
    const timeout = window.setTimeout(() => setIdlePanel('session'), SETUP_PANEL_DURATION_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [display, idlePanel, setupPanelRun]);

  const selectIdlePanel = (nextPanel: IdlePanel) => {
    setIdlePanel(nextPanel);
    if (nextPanel === 'setup') setSetupPanelRun((current) => current + 1);
  };

  useEffect(() => {
    if (!credential || !display || !selectedSessionId) return;
    let cancelled = false;
    setSession(null);
    setLapNumber(null);
    setSessionLoadError(null);
    setSessionLoading(true);
    void loadKioskSession(selectedSessionId)
      .then((payload) => {
        if (cancelled) return;
        if (!payload) {
          setSessionLoadError('This session does not contain supported telemetry.');
          return;
        }
        setSession(payload);
        setLapNumber(pickDefaultLapNumber(payload));
      })
      .catch(() => {
        if (!cancelled) setSessionLoadError('Unable to load this session.');
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [credential, display?.grantId, loadKioskSession, selectedSessionId]);

  useEffect(() => {
    if (!displayGrantId) return;
    const timers = startKioskIdleClearTimers({
      idleMs: IDLE_MS,
      warningMs: WARNING_MS,
      onWarning: () => setWarning(true),
      onClear: clearDisplay,
    });
    idleTimers.current = timers;
    timers.restart();
    const events = ['pointerdown', 'keydown', 'wheel'] as const;
    events.forEach((event) => window.addEventListener(event, resetIdle, { passive: true }));
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetIdle));
      timers.cancel();
      if (idleTimers.current === timers) idleTimers.current = undefined;
    };
  }, [clearDisplay, displayGrantId, resetIdle]);

  const sessionItem =
    sessions.find((item) => item.id === selectedSessionId) ??
    (display
      ? {
          id: display.session.id,
          title: display.session.trackName,
          subtitle: `${display.session.lapCount} laps`,
          recordedAtMs: display.session.recordedAtMs,
          trackName: display.session.trackName,
          driverId: null,
          driverName: display.session.driverName,
          driverAvatarUrl: null,
        }
      : null);
  const slot = useSessionLapSlot({
    sessionId: null,
    sessionExport: session,
    lapTimingMode: 'mychron',
    sessionTrackName: sessionItem?.trackName ?? null,
    readOnly: true,
    publicDemo: true,
  });

  if (error)
    return (
      <Center>
        <PaddiqLogo variant="brand" />
        <Typography variant="h4" fontWeight={900}>
          STATION UNAVAILABLE
        </Typography>
        <Typography color="text.secondary">{error}</Typography>
        <Button variant="contained" onClick={() => location.reload()}>
          Retry
        </Button>
      </Center>
    );
  if (!station)
    return (
      <Center>
        <PaddiqLogo variant="brand" />
        <CircularProgress />
        <Typography sx={{ letterSpacing: 2, fontWeight: 800 }}>STARTING STATION…</Typography>
      </Center>
    );
  const confirmationOverlay = pendingDisplay && (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,8,15,.96)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 10000,
        p: 4,
      }}
    >
      <Stack spacing={2.5} alignItems="center" sx={{ width: '100%', maxWidth: 520 }}>
        <Typography variant="overline" color="#A970FF">
          ACCOUNT SIGN-IN
        </Typography>
        <Typography variant="h3" fontWeight={900} textAlign="center">
          VERIFY YOUR IDENTITY
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          {pendingDisplay.session.driverName} · {pendingDisplay.session.trackName}
        </Typography>
        <TextField
          autoFocus
          placeholder="000000"
          value={confirmationCode}
          onChange={(event) =>
            setConfirmationCode(event.target.value.replace(/\D/g, '').slice(0, 6))
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') void confirmDisplay();
          }}
          inputProps={{
            'aria-label': 'Six-digit confirmation code',
            autoComplete: 'one-time-code',
            inputMode: 'numeric',
            maxLength: 6,
            style: {
              color: '#111827',
              WebkitTextFillColor: '#111827',
              caretColor: '#111827',
              textAlign: 'center',
              fontSize: 40,
              letterSpacing: 12,
              fontWeight: 900,
            },
          }}
          sx={{
            width: '100%',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#FFFFFF',
              color: '#111827',
              '& fieldset': { borderColor: '#D1D5DB' },
              '&:hover fieldset': { borderColor: '#9CA3AF' },
              '&.Mui-focused fieldset': { borderColor: '#8B5CF6', borderWidth: 3 },
            },
            '& .MuiInputBase-input::placeholder': { color: '#6B7280', opacity: 1 },
          }}
        />
        {confirmationError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {confirmationError}
          </Alert>
        )}
        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={confirmationCode.length !== 6 || confirmationSubmitting}
          onClick={() => void confirmDisplay()}
        >
          {confirmationSubmitting ? 'CONNECTING…' : 'CONNECT SESSION'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Your Paddiq account and exact session will open only after the correct code is entered.
        </Typography>
      </Stack>
    </Box>
  );

  if (accountTermination)
    return (
      <Center>
        <PaddiqLogo variant="brand" />
        <Typography variant="h3" fontWeight={950} textAlign="center">
          ACCOUNT CLOSED FOR PRIVACY
        </Typography>
        <Typography color="text.secondary" textAlign="center" maxWidth={620}>
          Customer information is cleared. This station will stay locked until the server confirms
          the exact account session is closed.
        </Typography>
        {isCorruptKioskAccountTerminationLock(accountTermination.lock) ? (
          <Alert severity="error">
            Secure sign-out recovery state is damaged. Customer data remains hidden and new handoffs
            are blocked until this kiosk is re-enrolled.
          </Alert>
        ) : station && station.id !== accountTermination.lock.stationId ? (
          <Alert severity="error">
            This browser is enrolled to a different station and cannot close the prior account
            session. Re-enroll the original station before accepting another customer.
          </Alert>
        ) : accountTermination.status === 'exhausted' ? (
          <Alert severity="warning">
            Secure sign-out could not be confirmed. Customer data remains hidden and new handoffs
            are blocked.
          </Alert>
        ) : accountTermination.status === 'retrying' ? (
          <Alert severity="warning">
            Connection unavailable. Secure sign-out is retrying with the exact session identity.
          </Alert>
        ) : (
          <Alert severity="info">Confirming secure sign-out with the server…</Alert>
        )}
        {accountTermination.status === 'exhausted' ? (
          <Button
            variant="contained"
            onClick={() => {
              setAccountTermination((current) =>
                current ? { ...current, status: 'clearing', attempt: 0 } : current,
              );
              setAccountTerminationRun((current) => current + 1);
            }}
          >
            RETRY SECURE SIGN OUT
          </Button>
        ) : (
          <CircularProgress size={28} />
        )}
      </Center>
    );

  if (clearState.lock)
    return (
      <Center>
        <PaddiqLogo variant="brand" />
        <Typography variant="h3" fontWeight={950} textAlign="center">
          SESSION CLOSED FOR PRIVACY
        </Typography>
        <Typography color="text.secondary" textAlign="center" maxWidth={620}>
          {clearState.status === 'retrying'
            ? 'Telemetry will stay hidden while this station reconnects and confirms the session is closed.'
            : 'Telemetry is hidden while this station confirms the session is closed.'}
        </Typography>
        {clearState.status === 'retrying' && (
          <Alert severity="warning">
            Connection unavailable. Secure close will retry automatically.
          </Alert>
        )}
        <CircularProgress size={28} />
      </Center>
    );

  if (!display)
    return (
      <Center>
        <PaddiqLogo variant="brand" />
        <Stack spacing={1} alignItems="center">
          <Typography
            variant="h2"
            fontWeight={950}
            textAlign="center"
            sx={{ letterSpacing: '-0.035em' }}
          >
            YOUR LAPS. BIGGER.
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center">
            Install Paddiq for your first session, or display data you have already pulled.
          </Typography>
        </Stack>
        <Stack spacing={1.75} alignItems="center" sx={{ width: '100%', maxWidth: 940 }}>
          <Tabs
            value={idlePanel}
            onChange={(_event, panel: IdlePanel) => selectIdlePanel(panel)}
            aria-label="Kiosk instructions"
            sx={{
              width: '100%',
              borderBottom: '1px solid rgba(148,163,184,.18)',
              '& .MuiTab-root': {
                minHeight: 46,
                px: 2,
                color: 'rgba(255,255,255,.55)',
                fontSize: '.9rem',
                fontWeight: 750,
              },
              '& .Mui-selected': { color: '#E9D5FF !important', fontWeight: 900 },
              '& .MuiTabs-indicator': {
                height: 3,
                bgcolor: '#8B5CF6',
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              value="setup"
              icon={<DownloadRoundedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Install & Set Up"
            />
            <Tab
              value="session"
              icon={<QrCode2RoundedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Session QR"
            />
          </Tabs>
          {idlePanel === 'setup' ? (
            <Box
              role="tabpanel"
              aria-label="Install and set up Paddiq"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '230px 1fr', md: '270px 1fr' },
                gap: 1.5,
                width: '100%',
              }}
            >
              <Stack
                spacing={1.15}
                alignItems="center"
                justifyContent="center"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: 'rgba(15,23,42,.72)',
                  border: '1px solid rgba(148,163,184,.18)',
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.8}
                  alignItems="center"
                  sx={{ alignSelf: 'stretch' }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 1.5,
                      bgcolor: 'rgba(139,92,246,.18)',
                      color: '#C4B5FD',
                    }}
                  >
                    <DownloadRoundedIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '.92rem', fontWeight: 900 }}>Get Paddiq</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '.78rem' }}>
                      iPhone · App Store
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  component="a"
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Get Paddiq for iPhone from the App Store"
                  sx={{ display: 'block', p: 0.8, bgcolor: 'white', borderRadius: 2.25 }}
                >
                  <Box
                    component="img"
                    src="/paddiq-ios-app-store-qr.svg"
                    alt="QR code for Paddiq on the iOS App Store"
                    sx={{ width: 174, height: 174, display: 'block' }}
                  />
                </Box>
                <Typography
                  color="text.secondary"
                  textAlign="center"
                  sx={{ fontSize: '.86rem', lineHeight: 1.45 }}
                >
                  Scan with your iPhone to download and install Paddiq.
                </Typography>
                <Box sx={{ px: 1, py: 0.4, borderRadius: 999, bgcolor: 'rgba(148,163,184,.09)' }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: 0.5 }}
                  >
                    ANDROID · COMING SOON
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1.15}>
                {[
                  {
                    icon: PersonAddAlt1RoundedIcon,
                    step: '1',
                    title: 'Create your account & join your team',
                    copy: 'Open Paddiq, sign in or create an account, then enter your team code if prompted.',
                  },
                  {
                    icon: SensorsRoundedIcon,
                    step: '2',
                    title: 'Connect your MyChron',
                    copy: 'Add your logger in Paddiq and complete the guided connection check.',
                  },
                  {
                    icon: SportsScoreRoundedIcon,
                    step: '3',
                    title: 'Go pull your session',
                    copy: 'At your kart, connect to MyChron and pull the data into Paddiq.',
                  },
                  {
                    icon: KeyboardReturnRoundedIcon,
                    step: '4',
                    title: 'Return to this kiosk',
                    copy: 'When the session opens in Paddiq, come back and choose Session QR.',
                    emphasis: true,
                  },
                ].map(({ icon: Icon, step, title, copy, emphasis }) => (
                  <Stack
                    key={step}
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{
                      p: 1.2,
                      borderRadius: 3,
                      bgcolor: emphasis ? 'rgba(124,58,237,.2)' : 'rgba(15,23,42,.72)',
                      border: emphasis
                        ? '1px solid rgba(167,139,250,.32)'
                        : '1px solid rgba(148,163,184,.18)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        bgcolor: emphasis ? '#8B5CF6' : 'rgba(139,92,246,.16)',
                        color: emphasis ? 'white' : '#C4B5FD',
                      }}
                    >
                      <Icon sx={{ fontSize: 21 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '.76rem',
                          color: '#A78BFA',
                          fontWeight: 900,
                          textTransform: 'uppercase',
                          letterSpacing: 0.8,
                        }}
                      >
                        {step} of 4
                      </Typography>
                      <Typography sx={{ fontSize: '1.02rem', fontWeight: 850 }}>{title}</Typography>
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: '.86rem', lineHeight: 1.45 }}
                      >
                        {copy}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ pt: 0.35 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.55 }}
                    >
                      <Typography color="text.secondary" sx={{ fontSize: '.78rem' }}>
                        Returning to Session QR
                      </Typography>
                      <Typography color="#C4B5FD" sx={{ fontSize: '.78rem', fontWeight: 900 }}>
                        {setupSecondsRemaining}s
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 3,
                        bgcolor: 'rgba(255,255,255,.08)',
                        borderRadius: 999,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${(setupSecondsRemaining / 30) * 100}%`,
                          bgcolor: '#8B5CF6',
                          transition: 'width 1s linear',
                        }}
                      />
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={<QrCode2RoundedIcon />}
                    onClick={() => selectIdlePanel('session')}
                    sx={{ px: 2, whiteSpace: 'nowrap' }}
                  >
                    Show session QR
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Stack
              role="tabpanel"
              aria-label="Session QR"
              spacing={1.4}
              alignItems="center"
              justifyContent="center"
            >
              <Typography sx={{ color: '#E9D5FF', fontSize: '1.05rem', fontWeight: 900 }}>
                Pair the session you pulled
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  p: 1.5,
                  borderRadius: '28px',
                  background: 'linear-gradient(135deg, #A855F7, #6D28D9)',
                  boxShadow: '0 24px 80px rgba(124,58,237,.35)',
                }}
              >
                {qr ? (
                  <Box
                    component="img"
                    src={qr.qrDataUrl}
                    alt="Paddiq kiosk pairing QR code"
                    sx={{
                      width: 280,
                      height: 280,
                      display: 'block',
                      bgcolor: 'white',
                      p: 1.5,
                      borderRadius: '20px',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'rgba(255,255,255,.08)',
                      borderRadius: '20px',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
              </Box>
              <Typography color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
                In Paddiq, open the session you pulled, tap Kiosk, and scan this code once. Enter
                the six-digit identity code from your phone here, and this screen will open that
                session in your account.
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              bgcolor: '#22C55E',
              boxShadow: '0 0 14px #22C55E',
            }}
          />
          <Typography sx={{ fontWeight: 850, letterSpacing: 1.4 }}>
            {station.name.toUpperCase()} · READY
          </Typography>
        </Stack>
        <Typography
          color={locationStatus === 'active' ? '#C4B5FD' : 'text.secondary'}
          textAlign="center"
          sx={{ fontSize: '.82rem', fontWeight: 700 }}
        >
          {locationStatus === 'active'
            ? 'LOCATION ACTIVE · THE KIOSK BUTTON APPEARS IN PADDIQ WITHIN 0.25 MI'
            : locationStatus === 'unavailable'
              ? 'LOCATION UNAVAILABLE · ENABLE BROWSER LOCATION TO SHOW THIS KIOSK NEARBY'
              : 'FINDING THIS KIOSK’S LOCATION…'}
        </Typography>
        {stationLocation && (
          <Stack spacing={0.35} alignItems="center" sx={{ mt: -2 }}>
            {locationLabel && (
              <Typography color="white" sx={{ fontSize: '.9rem', fontWeight: 850 }}>
                {locationLabel}
              </Typography>
            )}
            <Typography color="text.secondary" sx={{ fontSize: '.72rem', fontFamily: 'monospace' }}>
              {stationLocation.latitude.toFixed(5)}, {stationLocation.longitude.toFixed(5)} · ±
              {Math.round(stationLocation.accuracyMeters)} m
            </Typography>
          </Stack>
        )}
        {confirmationOverlay}
      </Center>
    );

  return (
    <Box
      sx={{
        height: '100dvh',
        bgcolor: '#020A12',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 1.25,
          minHeight: 72,
          borderBottom: '1px solid rgba(148,163,184,.16)',
          background: 'linear-gradient(90deg, #080B16 0%, #0D1020 55%, #160B2B 100%)',
        }}
      >
        <Stack direction="row" spacing={2.25} alignItems="center">
          <PaddiqLogo variant="sidebar" />
          <Box sx={{ width: '1px', height: 34, bgcolor: 'rgba(148,163,184,.22)' }} />
          <Box>
            <Typography variant="h5" fontWeight={900}>
              {sessionItem?.driverName}
            </Typography>
            <Typography color="#9FB0BF" sx={{ fontSize: '.82rem' }}>
              {sessionItem?.title} · {sessionItem?.subtitle}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => setDrawerOpen(true)}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,.34)', fontWeight: 850 }}
          >
            SESSION & LAPS
          </Button>
          <Button
            variant="contained"
            onClick={clearDisplay}
            sx={{
              bgcolor: '#8B5CF6',
              color: 'white',
              fontWeight: 900,
              '&:hover': { bgcolor: '#7C3AED' },
            }}
          >
            DONE
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {sessionLoading || !sessionItem ? (
          <Center>
            <CircularProgress />
            <Typography>LOADING TELEMETRY…</Typography>
          </Center>
        ) : sessionLoadError ? (
          <Center>
            <Typography variant="h4" fontWeight={900}>
              SESSION UNAVAILABLE
            </Typography>
            <Typography color="text.secondary">{sessionLoadError}</Typography>
            <Button variant="outlined" color="inherit" onClick={() => setDrawerOpen(true)}>
              CHOOSE ANOTHER SESSION
            </Button>
          </Center>
        ) : session && lapNumber == null ? (
          <Center>
            <Typography variant="h4" fontWeight={900}>
              NO LAPS DETECTED
            </Typography>
            <Typography color="text.secondary" textAlign="center" maxWidth={620}>
              The session downloaded successfully and contains{' '}
              {session.gps?.length?.toLocaleString() ?? 0} GPS samples, but the logger did not
              include usable lap markers.
            </Typography>
            <Button variant="outlined" color="inherit" onClick={() => setDrawerOpen(true)}>
              CHOOSE ANOTHER SESSION
            </Button>
          </Center>
        ) : session && lapNumber != null ? (
          <SessionsCompareMap
            publicDemo
            loadSessionExport={loadKioskSession}
            loadIdealLineReference={loadKioskIdealLineReference}
            idealLineAuthScope={display.grantId}
            viewKey={`${display.grantId}:${selectedSessionId}:${lapNumber}`}
            primarySessionId={selectedSessionId!}
            primarySessionExport={session}
            primaryLapNumber={lapNumber}
            onPrimaryLapChange={setLapNumber}
            lapTimingMode="mychron"
            primarySlot={slot}
            compareSlots={compareSlots}
            sessions={sessions.length ? sessions : [sessionItem]}
          />
        ) : (
          <Center>
            <Typography variant="h4" fontWeight={900}>
              SESSION UNAVAILABLE
            </Typography>
          </Center>
        )}
      </Box>
      {sessionItem && (
        <SessionSelectorDrawer
          publicDemo
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sessions={sessions}
          listLoading={sessions.length === 0 && sessionDirectory.status === 'loading'}
          error={sessions.length === 0 ? sessionDirectory.error : null}
          hasMoreSessions={kioskSessionDirectoryHasMore(sessionDirectory.directory)}
          loadingMoreSessions={sessionDirectory.status === 'loading-more'}
          loadMoreError={sessions.length > 0 ? sessionDirectory.error : null}
          sessionListSummary={
            sessionDirectory.directory
              ? `${sessionDirectory.directory.sessions.length} of ${sessionDirectory.directory.total} sessions loaded${kioskSessionDirectoryHasMore(sessionDirectory.directory) ? '' : ' · complete'}`
              : null
          }
          onLoadMoreSessions={loadMoreSessions}
          onRetrySessionList={reloadSessionDirectory}
          primarySessionId={selectedSessionId}
          primarySessionExport={session}
          primaryLapNumber={lapNumber}
          lapTimingMode="mychron"
          onLapTimingModeChange={() => {}}
          primarySlot={slot}
          onSessionSelectionChange={(sessionIds) => {
            const [primaryId, ...compareIds] = sessionIds;
            if (!primaryId) return;
            setSelectedSessionId(primaryId);
            setCompareSlots((current) => compareIds.map((sessionId) => (
              current.find((compareSlot) => compareSlot.sessionId === sessionId)
              ?? createCompareSlot(sessionId)
            )));
          }}
          onPrimaryLapChange={setLapNumber}
          compareSlots={compareSlots}
          onAddCompareSlot={() =>
            selectedSessionId
            && setCompareSlots((current) => [...current, createCompareSlot(selectedSessionId)])
          }
          onRemoveCompareSlot={(id) =>
            setCompareSlots((current) => current.filter((slot) => slot.id !== id))
          }
          onCompareSlotSessionChange={(id, sessionId) =>
            setCompareSlots((current) =>
              current.map((slot) =>
                slot.id === id ? { ...slot, sessionId, lapNumber: null } : slot,
              ),
            )
          }
          onCompareSlotLapChange={(id, nextLap) =>
            setCompareSlots((current) =>
              current.map((slot) => (slot.id === id ? { ...slot, lapNumber: nextLap } : slot)),
            )
          }
          compareSessionGroups={{ matching: sessions, other: [] }}
          onReset={() => {
            setSelectedSessionId(display.session.id);
            setCompareSlots([]);
          }}
          onSessionContextSaved={() => {}}
        />
      )}
      {warning && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,8,15,.94)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Typography variant="h2" fontWeight={900}>
              SESSION ENDING
            </Typography>
            <Typography variant="h5">
              This session will close in 15 seconds to protect your data.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" size="large" onClick={resetIdle}>
                CONTINUE SESSION
              </Button>
              <Button variant="outlined" size="large" onClick={clearDisplay}>
                DONE
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
      {confirmationOverlay}
    </Box>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      spacing={3}
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '100dvh',
        p: 4,
        color: 'white',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at 50% 15%, rgba(139,92,246,.22), transparent 34%), radial-gradient(circle at 15% 85%, rgba(76,29,149,.13), transparent 28%), linear-gradient(180deg, #080B16 0%, #020711 100%)',
      }}
    >
      {children}
    </Stack>
  );
}
